import { DefaultMessages, ProgramThemeMessages, SegmentSpecificMessages } from '@sheerid/jslib';
import { RecursivePartial } from '../types/types';


type LibraryMessages = DefaultMessages & SegmentSpecificMessages & ProgramThemeMessages;

/**
 * Feel free to modify this type for this project (only) here
 * For example, if you add a whole new block of text that the jslib does not have a spot for already
 *
 * This is not necessary if you are just overriding text for a pre-existing message id
 */
type ThisProjectMessages = {
    informationOutsourcedToSheerId: string,
    informationTransferredAndUsedForVerification: string,
    forMoreInformationAsics: string,
    optIn: string,
};

/**
 * Define the actual language strings here, as needed.
 * This object should be sparse, so you inherit language from the library. Only override what you need to.
 *
 * The complicated type ensures your object adheres to the library's type(s), as well as any additional type you've
 * created in ThisProjectMessages.
 */
export const messagesJP: RecursivePartial<LibraryMessages & ThisProjectMessages> = {
    informationOutsourcedToSheerId: "学生又は教職員のステータス認証の手続は、アシックスジャパン株式会社の提携パートナーであるSheerIDに委託しています。",
    informationTransferredAndUsedForVerification: "このフォームによりご提供いただいた個人情報は米国に送信され、<a href='https://www.sheerid.com/privacy_notice_services_japanese/' class='sid-link' target='_blank'>SheerIDのプライバシーポリシー</a>に基づいて、学生又は教職員のステータス認証及びクーポンコードの通知の目的に利用されます。 ",
    forMoreInformationAsics: "認証手続を通じてSheerIDよりアシックスジャパン株式会社に提供された個人情報の取り扱いについての詳細は、<a href='https://www.asics.com/jp/ja-jp/mk/support/guide/privacypolicy' class='sid-link' target='_blank'>アシックスプライバシーポリシー</a>をご覧ください。",
    schoolName: '高校 / 大学 / 短大名',
    firstName: '名',
    lastName: '姓',
    requestSchool: '学校がリストにない場合は追加リクエストができます',
    emailAddress: 'メールアドレス',
    emailExplanation: '学校が発行しているメールアドレスをご入力ください。',
    birthDate: '生年月日* <span class="sid-field__birthdate-label-with-explanation">認証目的のみで使用します。SheerID のプライバシーポリシーに記載のとおり、本学生/教職員割引プログラムは16歳以上の学生が対象となります。</span>',
    optIn: '上記個人情報の取り扱いについて同意します。',
    verifyMyStudentStatus: '学生ステータスを認証',
    verificationPurposesOnly: 'Used for verification purposes only. Offer is for Students age 16 and up only, as described in the SheerID Privacy Policy.',
    howDoesVerifyingWork: '認証手続きとは？',
    poweredBy: 'SheerID 提供による認証サービス',
    sheeridFaqs: 'SheerID よくあるご質問',
    infoShared1: "クリックすることにより、<a href='https://www.sheerid.com/privacy_notice_services_japanese/' class='sid-link' target='_blank'>SheerID のプライバシーポリシー</a>に記載のとおり、ステータス資格を認証する目的で個人情報を処理することに同意いたします。",
    infoShared2: 'SheerID に個人情報が共有されていることを理解しています。',
    informationTransferredToUS: "個人情報は米国に転送され、<a href='https://www.sheerid.com/privacy_notice_services_japanese/' class='sid-link' target='_blank'>SheerID のプライバシーポリシー</a>に基づいて処理されます。",
    step: {
        personalInfo: {
            title: "学生割引プログラムの申し込み",
            subtitle: "高校、学位取得可能な短大あるいは大学の学生であることを認証します。",
            howDoesVerifyingWorkDetails: 'この特典の対象となるには、現在、学位が付与された高等教育機関の               認定を受けている必要があります。               <br><br>                アシックスは、信頼できるパートナーであるSheerIDを使用して、現在在籍中の学生であることを検証します。SheerIDは、                資格の検証に役立つ情報のみを収集します。                追加情報またはドキュメントが要求される場合があります。',
        },
        sso: {
            title: '学校のログイン情報を利用して認証',
            subtitle: "学校のサイトにログインしてください。新しいタブで開きます。",
            subtitle2: 'サインインできませんか？',
            login: '自分の学校にログイン',
            cancel: '雇用証明書をアップロードします',
        },
        success: {
            title: '無事、完了です！学生ステータスが認証されました。',
            subtitle: '専用のクーポンコードです。あなただけの固有のコードで1回のみ使用可能です。 ',
            copyCouponCode: 'クーポンコードをコピー ',
            redirectButtonText: 'コードを今すぐ使う',
            emailNotification: 'クーポンコードのコピーをメールで送付しましたので、今すぐあるいは後で新しいコードを使うことことができます。verify@sheerid.com からのメールを検索してください。 ',
            verificationOnly: "SheerID は、認証プロセスのみ扱っています。認証が完了していますので、ここからは、プロモーションの規約やクーポンコードの使用などすべてのお問い合わせは、 ASICS カスタマーサービスに直接ご連絡ください。",
        },
        docUpload: {
            title: '学生証と時間割が必要です。',
            subtitle: '提携パートナーである SheerID が提出書類を48時間以内に確認いたします。',
            acceptedTypes: 'ファイル形式は、 .jpg、 .jpeg、 .png、 .pdf、 .gif、 .bmp で、サイズは1280ピクセル x 860ピクセル以上です。オリジナルの書類を編集したものは認められません。',
            addFile: "ファイルを追加 <span class='sid-add-file-extension'>(1回のアップロードの上限は3ファイル、合計10MB まで)</span>",
            submitButtonLabel: '送信',
            uploadInstructions: '以下の情報を含む、学校発行の書類をアップロードしてください。:',
            uploadInfo: {
                fullName: '氏名（姓名)',
                student: {
                    school: '学校名',
                    enrollmentDate: '現学期の在学を証明する日付 (または、過去3か月以内)',
                },
            },
            acceptableUploadExamples: '認められる書類の例:',
            acceptableUploads: {
                student: {
                    idCard: '有効期限が明記された学生証',
                    classSchedule: ' 大学名、氏名、学籍番号が記載された時間割',
                    tuitionReceipt: '大学名、氏名、学籍番号が記載された授業料の領収書',
                },
            },
            fileInstructions: 'ファイルをここにドラッグ・アンド・ドロップ、<span class="sid-dropzone-wrap--link sid-link">あるいは ブラウズします</a>',
        },
        pending: {
            titleReview: '確認中です。',
            turnaroundTime: '学生ステータスを確認するために、SheerID が提出書類を確認しています。このプロセスは、通常 {estReviewTime} かかります。まれに、リクエストが集中した場合には、 {maxReviewTime} 程度かかることもあります。',
            subtitle2: '結果が分かり次第、お知らせいたします。',
            subtitle3: 'メールにてご連絡いたします。',
        },
    },
};
